import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { cn } from "@/lib/utils";

interface MetricCardProps {
  title: string;
  value: string;
  icon: React.ElementType;
  hint?: string;
  current?: number;
  previous?: number;
  inverted?: boolean;
}

export function MetricCard({
  title,
  value,
  icon: Icon,
  hint,
  current,
  previous,
  inverted = false,
}: MetricCardProps) {
  const delta = computeDelta(current, previous);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-bold">{value}</p>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
        {delta !== null && (
          <DeltaPill percent={delta} inverted={inverted} />
        )}
      </CardContent>
    </Card>
  );
}

function computeDelta(
  current: number | undefined,
  previous: number | undefined,
): number | null {
  if (current === undefined || previous === undefined) {
    return null;
  }

  if (previous === 0) {
    if (current === 0) {
      return 0;
    }

    return current > 0 ? 100 : -100;
  }

  return ((current - previous) / previous) * 100;
}

function DeltaPill({
  percent,
  inverted,
}: {
  percent: number;
  inverted: boolean;
}) {
  const isFlat = Math.abs(percent) < 0.5;
  const isUp = percent > 0;
  const positive = inverted ? !isUp : isUp;
  const Icon = isFlat ? Minus : isUp ? ArrowUp : ArrowDown;
  const tone = isFlat
    ? "text-muted-foreground"
    : positive
      ? "text-green-600"
      : "text-red-600";

  return (
    <p className={cn("mt-1 flex items-center gap-1 text-xs", tone)}>
      <Icon className="h-3 w-3" />
      {isFlat ? "Flat" : `${Math.abs(percent).toFixed(1)}%`} vs previous
    </p>
  );
}
